import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useMobile } from '../hooks/useMobile';

const token = () => localStorage.getItem('token');
const fmt = (v) => `£${(parseFloat(v)||0).toLocaleString('en-GB', {minimumFractionDigits:2, maximumFractionDigits:2})}`;

export default function OwnerAnalytics() {
  const mobile = useMobile();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo]     = useState(`${year}-12-31`);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    setLoading(true); setError('');
    const params = new URLSearchParams({ from, to, months });
    fetch(`/api/owner/analytics?${params}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(setData)
      .catch(() => setError('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [from, to, months]);

  const ml = mobile ? 0 : 70;
  const pt = mobile ? 64 : 0;

  if (loading) return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F7FAFC' }}>
      <Sidebar />
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#718096' }}>Loading analytics…</div>
    </div>
  );

  if (error) return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F7FAFC' }}>
      <Sidebar />
      <div style={{ flex:1, padding:24, color:'#e74c3c' }}>{error}</div>
    </div>
  );

  const { leads, deals, conversionRate, monthlyDeals, monthlyLeads, topIntroducers } = data || {};

  // Simple bar chart using divs
  const maxFees = Math.max(...(monthlyDeals||[]).map(m => m.fees), 1);
  const maxLeads = Math.max(...(monthlyLeads||[]).map(m => m.leads), 1);

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F7FAFC' }}>
      <Sidebar />
      <div style={{ flex:1, marginLeft:ml, paddingTop:pt, minWidth:0 }}>

        {/* Header */}
        <div style={{ background:'#fff', borderBottom:'1px solid #E2E8F0', padding: mobile ? '16px' : '20px 40px' }}>
          <h1 style={{ margin:0, fontSize: mobile ? 18 : 24, fontWeight:700, color:'#0e1b2c' }}>Platform Analytics</h1>
          <p style={{ margin:'2px 0 0', fontSize:13, color:'#718096' }}>Live data across all companies</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ padding:'6px 10px', border:'1px solid #E2E8F0', borderRadius:6, fontSize:13, color:'#0e1b2c' }} />
          <span style={{ fontSize:13, color:'#718096' }}>to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ padding:'6px 10px', border:'1px solid #E2E8F0', borderRadius:6, fontSize:13, color:'#0e1b2c' }} />
          <select value={months} onChange={e => setMonths(e.target.value)}
            style={{ padding:'6px 10px', border:'1px solid #E2E8F0', borderRadius:6, fontSize:13, color:'#0e1b2c' }}>
            <option value={3}>3 month chart</option>
            <option value={6}>6 month chart</option>
            <option value={12}>12 month chart</option>
          </select>
        </div>

        <div style={{ padding: mobile ? '16px' : '32px 40px', maxWidth:1200 }}>

          {/* Top stat cards */}
          <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:16, marginBottom:32 }}>
            {[
              { label:'Total Leads',      value: leads?.total || 0,          accent:'#6366f1' },
              { label:'Conversion Rate',  value: `${conversionRate || 0}%`,  accent:'#10b981' },
              { label:'Total Deals',      value: deals?.total || 0,          accent:'#f59e0b' },
              { label:'Total Fees',       value: fmt(deals?.totalFees),      accent:'#0e1b2c' },
            ].map(c => (
              <div key={c.label} style={{
                background:'#fff', borderRadius:10, padding: mobile ? '16px' : '24px',
                border:'1px solid #E2E8F0', borderTop:`4px solid ${c.accent}`,
                boxShadow:'0 1px 4px rgba(0,0,0,0.05)',
              }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#a0aec0', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>{c.label}</div>
                <div style={{ fontSize: mobile ? 22 : 28, fontWeight:700, color:'#0e1b2c' }}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* Lead funnel */}
          <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E2E8F0', padding:'24px', marginBottom:32, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <h2 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700, color:'#0e1b2c' }}>Lead Funnel</h2>
            <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:12 }}>
              {[
                { label:'Submitted', value: leads?.submitted, color:'#6366f1', bg:'#eef2ff' },
                { label:'Approved',  value: leads?.approved,  color:'#10b981', bg:'#d1fae5' },
                { label:'Rejected',  value: leads?.rejected,  color:'#ef4444', bg:'#fee2e2' },
                { label:'Converted', value: leads?.converted, color:'#f59e0b', bg:'#fef3c7' },
              ].map(f => (
                <div key={f.label} style={{ background:f.bg, borderRadius:8, padding:'16px', textAlign:'center' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:f.color, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>{f.label}</div>
                  <div style={{ fontSize:28, fontWeight:700, color:f.color }}>{f.value || 0}</div>
                  <div style={{ fontSize:11, color:f.color, marginTop:4, opacity:0.7 }}>
                    {leads?.total ? Math.round(((f.value||0)/leads.total)*100) : 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly charts side by side */}
          <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:24, marginBottom:32 }}>

            {/* Monthly fees bar chart */}
            <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E2E8F0', padding:'24px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <h2 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700, color:'#0e1b2c' }}>Monthly Fees</h2>
              <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:120 }}>
                {(monthlyDeals||[]).map(m => (
                  <div key={m.month} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ fontSize:9, color:'#a0aec0', fontWeight:600 }}>{fmt(m.fees).replace('£','£')}</div>
                    <div style={{
                      width:'100%', borderRadius:'4px 4px 0 0',
                      background:'#10b981',
                      height: `${Math.max(4, Math.round((m.fees/maxFees)*90))}px`,
                      opacity: 0.85,
                    }} />
                    <div style={{ fontSize:9, color:'#718096', whiteSpace:'nowrap' }}>{m.month}</div>
                  </div>
                ))}
                {(!monthlyDeals||monthlyDeals.length===0) && (
                  <div style={{ color:'#a0aec0', fontSize:13, width:'100%', textAlign:'center' }}>No data yet</div>
                )}
              </div>
            </div>

            {/* Monthly leads bar chart */}
            <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E2E8F0', padding:'24px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <h2 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700, color:'#0e1b2c' }}>Monthly Leads</h2>
              <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:120 }}>
                {(monthlyLeads||[]).map(m => (
                  <div key={m.month} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ fontSize:9, color:'#a0aec0', fontWeight:600 }}>{m.leads}</div>
                    <div style={{
                      width:'100%', borderRadius:'4px 4px 0 0',
                      background:'#6366f1',
                      height: `${Math.max(4, Math.round((m.leads/maxLeads)*90))}px`,
                      opacity: 0.85,
                    }} />
                    <div style={{ fontSize:9, color:'#718096', whiteSpace:'nowrap' }}>{m.month}</div>
                  </div>
                ))}
                {(!monthlyLeads||monthlyLeads.length===0) && (
                  <div style={{ color:'#a0aec0', fontSize:13, width:'100%', textAlign:'center' }}>No data yet</div>
                )}
              </div>
            </div>
          </div>

          {/* Deal stats + Top introducers */}
          <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:24, marginBottom:32 }}>

            {/* Deal breakdown */}
            <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E2E8F0', padding:'24px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <h2 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700, color:'#0e1b2c' }}>Deal Breakdown</h2>
              {[
                { label:'Pipeline',  value: deals?.pipeline,  color:'#f59e0b' },
                { label:'Completed', value: deals?.completed, color:'#10b981' },
                { label:'Avg Fee',   value: fmt(deals?.avgFee), color:'#6366f1' },
              ].map(d => (
                <div key={d.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid #EDF2F7' }}>
                  <div style={{ fontSize:14, color:'#4b5a6e' }}>{d.label}</div>
                  <div style={{ fontSize:16, fontWeight:700, color:d.color }}>{d.value}</div>
                </div>
              ))}
            </div>

            {/* Top introducers */}
            <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E2E8F0', padding:'24px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <h2 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700, color:'#0e1b2c' }}>Top Introducers</h2>
              {(topIntroducers||[]).length === 0 && (
                <div style={{ color:'#a0aec0', fontSize:13 }}>No introducer data yet</div>
              )}
              {(topIntroducers||[]).map((intro, i) => (
                <div key={intro.name} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #EDF2F7' }}>
                  <div style={{ width:24, height:24, borderRadius:'50%', background:'#EDF2F7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#718096', flexShrink:0 }}>
                    {i+1}
                  </div>
                  <div style={{ flex:1, fontSize:14, fontWeight:500, color:'#0e1b2c', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{intro.name}</div>
                  <div style={{ fontSize:13, color:'#718096', flexShrink:0 }}>{intro.deals} deals</div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#10b981', flexShrink:0 }}>{fmt(intro.fees)}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
